import React, { useState } from 'react';
import { FaMapMarkerAlt, FaEnvelope } from 'react-icons/fa';
import { FaThreads } from 'react-icons/fa6';
import axios from 'axios';
import Botpoison from '@botpoison/browser';
import './Contact.css';
import IconWrapper from './IconWrapper';

// Create a new Botpoison instance with your public key
const botpoison = new Botpoison({
  publicKey: "pk_96c3b628-7a73-44f5-bb7f-ee58d3722f64"
});

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    officeType: '',
    people: '',
    moveIn: '',
    message: '',
    privacy: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.privacy) {
      alert('Please agree to the privacy policy to submit the form.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      // Submit to Formspark
      const response = await axios.post('https://submit-form.com/lJs7xi5or', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        officeType: formData.officeType,
        people: formData.people,
        moveIn: formData.moveIn,
        message: formData.message,
        privacy: formData.privacy,
        _botpoison: solution, // Forward the Botpoison solution
        _subject: 'New Inquiry from The Collective at Gateway Website'
      });

      if (response.status === 200) {
        setSubmitMessage('Thank you! Your inquiry has been submitted successfully. We\'ll get back to you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          officeType: '',
          people: '',
          moveIn: '',
          message: '',
          privacy: false
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitMessage('There was an error submitting your form. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="section-header">
          <h2>Let's Connect!</h2>
        </div>

        <div className="contact-grid">
          <div className="contact-form-container">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your Name"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Your Email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Your Phone"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="company">Company (Optional)</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your Company"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="officeType">Office Space Interest *</label>
                  <select
                    id="officeType"
                    name="officeType"
                    value={formData.officeType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Office Type</option>
                    <option value="dedicated-desk">Dedicated Desk</option>
                    <option value="private-internal">Private Internal Office</option>
                    <option value="private-window">Private Window Office</option>
                    <option value="custom">Custom Solution</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="people">Number of People *</label>
                  <select
                    id="people"
                    name="people"
                    value={formData.people}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select</option>
                    <option value="1">1 Person</option>
                    <option value="2-3">2-3 People</option>
                    <option value="4-6">4-6 People</option>
                    <option value="7+">7+ People</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="moveIn">Desired Move-in Timeframe *</label>
                <select
                  id="moveIn"
                  name="moveIn"
                  value={formData.moveIn}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Timeframe</option>
                  <option value="immediate">Immediate</option>
                  <option value="1-month">Within 1 Month</option>
                  <option value="2-3-months">2-3 Months</option>
                  <option value="future">Future Planning</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Additional Comments</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell us more about your requirements"
                ></textarea>
              </div>

              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="privacy"
                  name="privacy"
                  checked={formData.privacy}
                  onChange={handleCheckboxChange}
                  required
                />
                <label htmlFor="privacy">
                  I agree to the privacy policy and consent to being contacted about my inquiry
                </label>
              </div>

              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
              </button>

              {submitMessage && (
                <div className={`submit-message ${submitMessage.includes('error') ? 'error' : 'success'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>

          <div className="contact-info">
            <div className="contact-card">
              <h3>Contact Information</h3>

              <div className="info-item">
                <IconWrapper icon={FaMapMarkerAlt} />
                <div>
                  <h4>Address</h4>
                  <p>1810 Gateway Dr, San Mateo, CA 94404</p>
                </div>
              </div>

              <div className="info-item">
                <IconWrapper icon={FaEnvelope} />
                <div>
                  <h4>Email</h4>
                  <p><a href="mailto:smg@scpropsm.com">smg@scpropsm.com</a></p>
                </div>
              </div>

              <div className="info-item">
                <IconWrapper icon={FaThreads} />
                <div>
                  <h4>Threads</h4>
                  <p><a href="https://www.scpropsm.com/" target="_blank" rel="noopener noreferrer">www.scpropsm.com</a></p>
                </div>
              </div>
            </div>

            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3164.0599391974257!2d-122.29784492392758!3d37.5429359272756!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808f9f3156c83f8f%3A0x1b9e2a5c0d2c1e1a!2s1810%20Gateway%20Dr%2C%20San%20Mateo%2C%20CA%2094404!5e0!3m2!1sen!2sus!4v1647882655590!5m2!1sen!2sus"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                title="The Collective at Gateway Location"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 